// İSG uzmanı portalı — Güvenlik brifingi yapılandırma (api-contract §11.2, firma tekil).
// `GET /visits/safety-briefing` → i18n metin (tr/en/de) + sürüm göster → düzenle →
// `PUT /visits/safety-briefing` (upsert; `version` otomatik artar). `contentTr` boş →
// `400 VALIDATION_FAILED`. Tek form, liste yok (SettingsCompanyPage deseni). Sorgu
// anahtarı erişim sondası (gate) + check-in ile PAYLAŞILIR — kaydedince cache güncellenir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Spinner,
  Tag,
  Textarea,
  resolveApiErrorMessage,
  visitsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { visitsKeys } from '../../lib/visitsKeys';

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel diller backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function SafetyBriefingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  // Gate + check-in ile PAYLAŞILAN sorgu (tek istek) — brifing metnini forma besler.
  const query = useQuery({
    queryKey: visitsKeys.safetyBriefing(),
    queryFn: () => visitsApi.getSafetyBriefing(),
  });

  const schema = useMemo(
    () =>
      z.object({
        contentTr: z.string().trim().min(1, t('visitors.validation.contentTrRequired')),
        contentEn: z.string().trim().optional(),
        contentDe: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const briefing = query.data;
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      contentTr: briefing?.contentTr ?? '',
      contentEn: briefing?.contentEn ?? '',
      contentDe: briefing?.contentDe ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      visitsApi.updateSafetyBriefing({
        contentTr: values.contentTr.trim(),
        contentEn: nullable(values.contentEn),
        contentDe: nullable(values.contentDe),
      }),
    onSuccess: (updated) => {
      // Yeni brifing (artmış sürüm) cache'e yazılır — gate + check-in aynı anahtarı okur.
      queryClient.setQueryData(visitsKeys.safetyBriefing(), updated);
      setMessage(t('visitors.briefing.updated'));
    },
  });

  const version = briefing?.version ?? 0;

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card>
        <CardContent>
          {query.isPending ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Spinner label={t('visitors.common.loading')} />
            </div>
          ) : query.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
          ) : (
            <form
              className="flex max-w-3xl flex-col gap-4"
              onSubmit={handleSubmit((values) => {
                setMessage(null);
                mutation.mutate(values);
              })}
              noValidate
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted">{t('visitors.briefing.subtitle')}</p>
                <Tag color="gray">
                  {version > 0
                    ? t('visitors.briefing.currentVersion', { version })
                    : t('visitors.briefing.notConfigured')}
                </Tag>
              </div>

              {mutation.isError ? (
                <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
              ) : null}

              <Field
                htmlFor="briefing-tr"
                label={t('visitors.briefing.field.tr')}
                error={formState.errors.contentTr?.message}
                required
              >
                <Textarea
                  id="briefing-tr"
                  rows={6}
                  invalid={Boolean(formState.errors.contentTr)}
                  placeholder={t('visitors.briefing.placeholder')}
                  {...register('contentTr')}
                />
              </Field>

              <Field htmlFor="briefing-en" label={t('visitors.briefing.field.en')}>
                <Textarea
                  id="briefing-en"
                  rows={6}
                  placeholder={t('visitors.briefing.placeholder')}
                  {...register('contentEn')}
                />
              </Field>

              <Field htmlFor="briefing-de" label={t('visitors.briefing.field.de')}>
                <Textarea
                  id="briefing-de"
                  rows={6}
                  placeholder={t('visitors.briefing.placeholder')}
                  {...register('contentDe')}
                />
              </Field>

              <div className="flex items-center justify-end pt-1">
                <Button type="submit" loading={mutation.isPending}>
                  {mutation.isPending ? (
                    <Spinner label={t('visitors.briefing.save')} />
                  ) : (
                    t('visitors.briefing.save')
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
